/* =========================================================================
   ClueVine — Bramble (word search)
   Hidden words tucked into a letter grid. Tap a word's first letter, then its
   last, and any straight line (across, down, or diagonal — read either way) that
   spells a placed word is found. Tap-to-tap means every cell is a real button,
   so it's fully keyboard-accessible. A different input paradigm entirely — proof
   the engine isn't tied to typing.
   ========================================================================= */

import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  PuzzleStatus,
  Prng,
  ResultSummary,
} from '../../engine/types';

export interface BrambleConfig {
  size: number;
  count: number;
}

export interface BrambleState {
  size: number;
  grid: string[]; // size*size, flattened
  words: string[];
  placements: Record<string, number[]>; // word -> ordered flat cell indices
  found: string[];
  selStart: number | null;
  revealedCell: number | null;
  finished: boolean;
}

export type BrambleMove = { type: 'tap'; index: number } | { type: 'clear' } | { type: 'finish' };

export const BRAMBLE_DIFFICULTIES: DifficultyLevel<BrambleConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '7×7 · 5 words', scoreMultiplier: 0.9, config: { size: 7, count: 5 } },
  { id: 'standard', label: 'Garden', blurb: '8×8 · 7 words', scoreMultiplier: 1, config: { size: 8, count: 7 } },
  { id: 'hard', label: 'Thicket', blurb: '9×9 · 9 words', scoreMultiplier: 1.3, config: { size: 9, count: 9 } },
];

// right, down, down-right, up-right (reverse reading handled at match time)
const DIRS: [number, number][] = [
  [0, 1],
  [1, 0],
  [1, 1],
  [-1, 1],
];
const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';

const rc = (size: number, index: number) => [Math.floor(index / size), index % size] as const;
const flat = (size: number, r: number, c: number) => r * size + c;

function placeWord(grid: string[], size: number, word: string, prng: Prng): number[] | null {
  const n = word.length;
  const dirs = prng.shuffle(DIRS);
  for (const [dr, dc] of dirs) {
    const rMin = dr < 0 ? n - 1 : 0;
    const rMax = dr > 0 ? size - n : size - 1;
    const cMin = dc < 0 ? n - 1 : 0;
    const cMax = dc > 0 ? size - n : size - 1;
    if (rMin > rMax || cMin > cMax) continue;
    for (let attempt = 0; attempt < 8; attempt++) {
      const r = prng.range(rMin, rMax);
      const c = prng.range(cMin, cMax);
      const cells: number[] = [];
      let ok = true;
      for (let k = 0; k < n; k++) {
        const cr = r + dr * k;
        const cc = c + dc * k;
        const idx = flat(size, cr, cc);
        if (grid[idx] !== '' && grid[idx] !== word[k]) {
          ok = false;
          break;
        }
        cells.push(idx);
      }
      if (ok) {
        for (let k = 0; k < n; k++) grid[cells[k]] = word[k];
        return cells;
      }
    }
  }
  return null;
}

export function generate(ctx: GenerateContext<BrambleConfig>): BrambleState {
  const { size, count } = ctx.difficulty.config;
  const grid = new Array<string>(size * size).fill('');
  const placements: Record<string, number[]> = {};
  const words: string[] = [];

  const maxLen = Math.min(7, size - 1);
  const candidates: string[] = [];
  for (let len = 4; len <= maxLen; len++) candidates.push(...ctx.dictionary.answersByLength(len));
  const pool = ctx.prng.shuffle(candidates);

  for (const word of pool) {
    if (words.length >= count) break;
    if (placements[word]) continue;
    const cells = placeWord(grid, size, word, ctx.prng);
    if (cells) {
      placements[word] = cells;
      words.push(word);
    }
  }

  for (let i = 0; i < grid.length; i++) {
    if (grid[i] === '') grid[i] = ALPHABET[ctx.prng.int(26)];
  }

  return { size, grid, words, placements, found: [], selStart: null, revealedCell: null, finished: false };
}

export function lineCells(size: number, start: number, end: number): number[] | null {
  const [r1, c1] = rc(size, start);
  const [r2, c2] = rc(size, end);
  const dr = Math.sign(r2 - r1);
  const dc = Math.sign(c2 - c1);
  const lenR = Math.abs(r2 - r1);
  const lenC = Math.abs(c2 - c1);
  const straight = r1 === r2 || c1 === c2 || lenR === lenC;
  if (!straight) return null;
  const steps = Math.max(lenR, lenC);
  const cells: number[] = [];
  for (let k = 0; k <= steps; k++) cells.push(flat(size, r1 + dr * k, c1 + dc * k));
  return cells;
}

function seqEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export function foundCells(state: BrambleState): Set<number> {
  const set = new Set<number>();
  for (const w of state.found) for (const idx of state.placements[w]) set.add(idx);
  return set;
}

export function status(state: BrambleState): PuzzleStatus {
  if (state.words.length > 0 && state.found.length === state.words.length) return 'won';
  if (state.finished) return state.found.length > 0 ? 'won' : 'lost';
  return 'playing';
}

export function validateMove(): { ok: boolean; reason?: string } {
  return { ok: true };
}

export function reduce(state: BrambleState, move: BrambleMove): BrambleState {
  switch (move.type) {
    case 'tap': {
      if (state.selStart === null) {
        return { ...state, selStart: move.index, revealedCell: null };
      }
      const cells = lineCells(state.size, state.selStart, move.index);
      if (!cells) return { ...state, selStart: null };
      const reversed = [...cells].reverse();
      for (const word of state.words) {
        if (state.found.includes(word)) continue;
        const placed = state.placements[word];
        if (seqEqual(placed, cells) || seqEqual(placed, reversed)) {
          return { ...state, found: [...state.found, word], selStart: null };
        }
      }
      return { ...state, selStart: null };
    }
    case 'clear':
      return { ...state, selStart: null };
    case 'finish':
      return { ...state, finished: true, selStart: null };
    default:
      return state;
  }
}

export function moveFeedback(prev: BrambleState, next: BrambleState, move: BrambleMove): MoveFeedback | null {
  if (next.found.length > prev.found.length) {
    const word = next.found[next.found.length - 1];
    const left = next.words.length - next.found.length;
    return {
      message: `Found ${word.toUpperCase()}${left > 0 ? ` — ${left} to go` : ''}`,
      tone: 'good',
    };
  }
  if (move.type === 'tap' && prev.selStart !== null && prev.selStart !== move.index) {
    return { message: 'No hidden word along that line', tone: 'info' };
  }
  return null;
}

// Word search uses cell taps, not the letter keyboard.
export function handleKey(_state: BrambleState, _key: InputKey): BrambleMove | null {
  return null;
}

export function makeHint(state: BrambleState): HintOutcome<BrambleState> | null {
  if (status(state) !== 'playing') return null;
  const remaining = state.words.filter((w) => !state.found.includes(w));
  if (remaining.length === 0) return null;
  const word = remaining[0];
  const startCell = state.placements[word][0];
  return {
    message: `A ${word.length}-letter word starts in the highlighted cell.`,
    penalty: 40,
    state: { ...state, revealedCell: startCell },
  };
}

export function score(state: BrambleState): ModeScore {
  const allFound = status(state) === 'won';
  const base = state.found.length * 110 + (allFound ? state.size * 20 : 0);
  if (state.found.length === 0) {
    return { base: 0, lines: [{ label: 'No words found', value: 0, kind: 'base' }], parMs: state.words.length * 13000 };
  }
  return {
    base,
    lines: [{ label: `${state.found.length}/${state.words.length} words found`, value: base, kind: 'base' }],
    parMs: state.words.length * 13000,
    perfect: allFound,
  };
}

export function summarize(state: BrambleState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `Found all ${state.words.length} words!` : `Found ${state.found.length}/${state.words.length}`,
    detail: state.words.map((w) => w.toUpperCase()).join(', '),
    revealLabel: 'Words',
    shareGrid: [`🔍 ${state.found.length}/${state.words.length} words`],
  };
}

export function deserialize(raw: unknown): BrambleState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<BrambleState>;
  if (typeof s.size !== 'number' || !Array.isArray(s.grid) || !Array.isArray(s.words)) return null;
  if (!s.placements || typeof s.placements !== 'object' || !Array.isArray(s.found)) return null;
  return {
    size: s.size,
    grid: s.grid.filter((c): c is string => typeof c === 'string'),
    words: s.words.filter((w): w is string => typeof w === 'string'),
    placements: s.placements as Record<string, number[]>,
    found: s.found.filter((w): w is string => typeof w === 'string'),
    selStart: typeof s.selStart === 'number' ? s.selStart : null,
    revealedCell: typeof s.revealedCell === 'number' ? s.revealedCell : null,
    finished: Boolean(s.finished),
  };
}
